/**
 * Four-point projective transform solver, used by the artwork crop editor to
 * flatten a quadrilateral marked on a skewed scan into an upright rectangle.
 *
 * SYNC: ported verbatim from `mobileJarvis/src/shared/utils/homography.ts`
 * (shipped Sep 2026). The file has no imports and no platform dependencies,
 * so the two copies are identical by construction — the test cases are
 * mirrored too. Fix a bug in one, fix it in the other.
 *
 * What does NOT port is applying the matrix. Mobile hands it to Skia;
 * `CanvasRenderingContext2D.setTransform` is affine-only (2x3) and cannot
 * consume the perspective terms, so the browser pass lives in `imageWarp.ts`.
 *
 * No DOM imports — unit-testable in node.
 */

export interface Point2D {
  x: number
  y: number
}

/** Corner order: TL, TR, BR, BL */
export type Quad = readonly [Point2D, Point2D, Point2D, Point2D]

/**
 * Computes the 3x3 projective transform (homography) mapping each src[i] to
 * dst[i]. Returns the matrix as 9 numbers in row-major order —
 * [m00, m01, m02, m10, m11, m12, m20, m21, m22]. Normalized so m22 === 1.
 * Returns null when the system is degenerate (e.g. three collinear corners,
 * duplicate points) and no unique homography exists.
 */
export const computeHomography = (src: Quad, dst: Quad): number[] | null => {
  // Build the 8x8 system Ah = b where h = [h00,h01,h02,h10,h11,h12,h20,h21]
  // and h22 is pinned to 1 to remove the scale ambiguity inherent to
  // projective transforms (otherwise the system is underdetermined).
  //
  // Each correspondence (x,y) -> (u,v) contributes two rows:
  //   [-x, -y, -1,  0,  0,  0, u*x, u*y] h = -u
  //   [ 0,  0,  0, -x, -y, -1, v*x, v*y] h = -v

  const A: number[][] = []
  const b: number[] = []

  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i]
    const { x: u, y: v } = dst[i]

    A.push([-x, -y, -1, 0, 0, 0, u * x, u * y])
    b.push(-u)

    A.push([0, 0, 0, -x, -y, -1, v * x, v * y])
    b.push(-v)
  }

  // Augment A with b for in-place Gaussian elimination
  const n = 8
  const M: number[][] = A.map((row, i) => [...row, b[i]])

  // Compute a rough scale for the epsilon threshold so it adapts to
  // the magnitude of the input coordinates (avoids false-degenerate on
  // large-coordinate quads and false-pass on nearly-degenerate tiny ones).
  let maxVal = 0
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      const v = Math.abs(M[r][c])
      if (v > maxVal) maxVal = v
    }
  }
  const eps = 1e-10 * (maxVal || 1)

  // Gaussian elimination with partial pivoting
  for (let col = 0; col < n; col++) {
    // Find the row with the largest absolute value in this column
    let pivotRow = col
    let pivotVal = Math.abs(M[col][col])
    for (let row = col + 1; row < n; row++) {
      const v = Math.abs(M[row][col])
      if (v > pivotVal) {
        pivotVal = v
        pivotRow = row
      }
    }

    if (pivotVal < eps) return null

    // Swap rows
    if (pivotRow !== col) {
      ;[M[col], M[pivotRow]] = [M[pivotRow], M[col]]
    }

    const pivot = M[col][col]
    for (let row = col + 1; row < n; row++) {
      const factor = M[row][col] / pivot
      for (let c = col; c <= n; c++) {
        M[row][c] -= factor * M[col][c]
      }
    }
  }

  // Back-substitution
  const h = new Array<number>(n)
  for (let row = n - 1; row >= 0; row--) {
    let sum = M[row][n]
    for (let c = row + 1; c < n; c++) {
      sum -= M[row][c] * h[c]
    }
    h[row] = sum / M[row][row]
  }

  // h = [h00,h01,h02,h10,h11,h12,h20,h21], h22 === 1
  const result = [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1]

  // A degenerate dst (e.g. duplicate points) can still yield a unique solution
  // to the linear system while producing a rank-deficient matrix that squashes
  // src points onto fewer than 4 dst points. Catch this via the determinant.
  const det =
    result[0] * (result[4] * result[8] - result[5] * result[7]) -
    result[1] * (result[3] * result[8] - result[5] * result[6]) +
    result[2] * (result[3] * result[7] - result[4] * result[6])

  if (Math.abs(det) < eps) return null

  return result
}

/** Applies a row-major 3x3 homography to a point, with the perspective divide. */
export const applyHomography = (matrix: number[], point: Point2D): Point2D => {
  const w = matrix[6] * point.x + matrix[7] * point.y + matrix[8]
  return {
    x: (matrix[0] * point.x + matrix[1] * point.y + matrix[2]) / w,
    y: (matrix[3] * point.x + matrix[4] * point.y + matrix[5]) / w,
  }
}
